// Function index:
//   MapStyle                    — 'painterly' | 'iso' | 'plain'.
//   mapStyle()                  — the active map style. Defaults to
//                                 'painterly' (flat grid + terrain art);
//                                 'iso'/'plain' are opt-in from the options
//                                 gear (localStorage `rz.mapstyle`), with
//                                 `?map=` as a one-off override.
//   loadHexAtlas(terrains)      — load the art the given terrains need, and
//                                 nothing else, via Pixi Assets. Idempotent per
//                                 frame, so it's safe and cheap to call again as
//                                 new terrain streams in. Resolves false (vector
//                                 fallback) when the flag is off or no tile
//                                 resolved. Terrain has two supply modes: a
//                                 packed spritesheet (terrain.json) OR — when
//                                 that's absent — individual per-terrain PNGs at
//                                 /atlases/terrain/<name>.png (no packer needed).
//   getTerrainTexture(terrain)  — the WHOLE tile canvas (face + headroom), or
//                                 null. What IsoRenderer draws — the headroom
//                                 is the tile's height.
//   getTerrainFaceTexture(t)    — the canvas cropped to its hex face, or null.
//                                 What the flat/painterly map draws, so the art
//                                 fills its hex instead of sitting small and
//                                 off-centre. Cached per frame.
//   getTokenTexture(key)        — Texture for a token frame, or null (→ circle).
//   terrainTilesLoaded(terrains) — true iff every distinct terrain in the given
//                                 set has a loaded texture (getTerrainTexture
//                                 non-null). Gates iso *selection* in
//                                 hex-scene.ts: a world whose terrains have no
//                                 art (interiors — floor/wall/door/feature)
//                                 renders flat wholesale rather than as
//                                 all-vector iso hexes.
//
// The six overworld terrain tiles DO ship in the repo (web/public/atlases/
// terrain/*.png) — the default 'painterly' style draws them, so the art is a
// hard dependency, not an optional extra. The tokens atlas IS optional: absent,
// tokens render as coloured circles. IsoRenderer additionally carries a
// per-tile vector fallback for any terrain that slips through the gate
// (streamed hexes), so a missing texture degrades to a flat-shaded iso hex.

import { Assets, Rectangle, Texture } from 'pixi.js';
import { TERRAIN_TEXTURE } from './palette.js';

// Per-FRAME cache rather than one global "did we load?" latch: a world only
// fetches the terrains it actually shows, so an interior (floor/wall/door/
// feature — none of which have art) costs zero bytes. A latch would let the
// first narrow world short-circuit every later one, stranding an overworld
// with no tiles at all. `null` means "fetched, absent" — distinct from a
// missing key, which means "never asked".
const terrainFrames = new Map<string, Texture | null>();
let sheetAttempted = false;
let sheetFrames: Record<string, Texture> | null = null;
let tokensAttempted = false;
let tokenFrames: Record<string, Texture> | null = null;

export type MapStyle = 'painterly' | 'iso' | 'plain';

const MAP_STYLES: readonly string[] = ['painterly', 'iso', 'plain'];

// Read once per call so changing the option takes effect on the next mount.
// 'painterly' is the default: the flat hex grid with the terrain art drawn into
// each hex. 'iso' is the isometric projection, 'plain' the art-free vector map;
// both are opt-in from the in-game options gear (persisted as `rz.mapstyle`).
// `?map=` overrides for a one-off look without touching the saved preference.
// Anything unrecognised — including a blocked localStorage — falls back to the
// default rather than leaving the map in an undefined state.
export function mapStyle(): MapStyle {
  try {
    const flag = new URLSearchParams(window.location.search).get('map');
    if (flag && MAP_STYLES.includes(flag)) return flag as MapStyle;
    const stored = window.localStorage.getItem('rz.mapstyle');
    if (stored && MAP_STYLES.includes(stored)) return stored as MapStyle;
    return 'painterly';
  } catch {
    return 'painterly';
  }
}

// Load the art the given terrains need, and nothing else. Safe and cheap to
// call again as new terrain streams in — already-resolved frames are never
// re-fetched. Returns true when at least one terrain frame is loaded.
export async function loadHexAtlas(terrains: Iterable<string> = []): Promise<boolean> {
  if (mapStyle() === 'plain') return false;

  const wanted = new Set<string>();
  for (const t of terrains) {
    const frame = TERRAIN_TEXTURE[t];
    if (frame && !terrainFrames.has(frame)) wanted.add(frame);
  }

  if (wanted.size > 0) {
    // Prefer a packed spritesheet; fall back to individual per-terrain PNGs so
    // art can be dropped in without a TexturePacker step. Probed at most once,
    // and only when something is actually missing.
    if (!sheetAttempted) {
      sheetAttempted = true;
      sheetFrames = await tryLoad('/atlases/terrain.json');
      if (sheetFrames) {
        for (const [name, tex] of Object.entries(sheetFrames)) terrainFrames.set(name, tex);
      }
    }
    for (const name of wanted) {
      if (terrainFrames.has(name)) continue; // supplied by the packed sheet
      terrainFrames.set(name, await tryLoadOne(`/atlases/terrain/${name}.png`));
    }
  }

  if (!tokensAttempted) {
    tokensAttempted = true;
    tokenFrames = await tryLoad('/atlases/tokens.json');
  }

  for (const tex of terrainFrames.values()) if (tex) return true;
  return false;
}

export function getTerrainTexture(terrain: string): Texture | null {
  const frame = TERRAIN_TEXTURE[terrain];
  return frame ? terrainFrames.get(frame) ?? null : null;
}

// The tile canvas is 256x384: a 256x254 hex face whose centre sits at y=256,
// plus transparent headroom above it for peaks and treetops (the contract in
// web/public/atlases/README.md). The ISO renderer wants that whole canvas — the
// headroom is the tile's height. The flat/painterly map does not: it fills a
// hex-sized box, so handing it the full canvas draws the face small and
// off-centre. Crop to the face and the existing fill-the-hex sizing lines up.
const ART_CANVAS_W = 256;
const ART_CANVAS_HEIGHT = 384;
const FACE_H_SRC = 254;
const FACE_TOP_SRC = 129; // face centre 256 − half-height 127
const faceFrames = new Map<string, Texture>();

export function getTerrainFaceTexture(terrain: string): Texture | null {
  const frame = TERRAIN_TEXTURE[terrain];
  if (!frame) return null;
  const cached = faceFrames.get(frame);
  if (cached) return cached;

  const full = getTerrainTexture(terrain);
  if (!full) return null; // not loaded yet — don't cache a miss

  // Art that isn't the contract shape gets used whole rather than sliced blind:
  // a frame outside the source bounds would throw, and a wrong crop is worse
  // than an unaligned tile.
  const face = full.width === ART_CANVAS_W && full.height === ART_CANVAS_HEIGHT
    ? new Texture({
      source: full.source,
      frame: new Rectangle(0, FACE_TOP_SRC, ART_CANVAS_W, FACE_H_SRC),
    })
    : full;
  faceFrames.set(frame, face);
  return face;
}

// Test-only: the face cache is keyed by frame name and never invalidated in
// production (a loaded texture never changes shape).
export function __resetFaceCacheForTest(): void {
  faceFrames.clear();
}

export function getTokenTexture(key: string | null | undefined): Texture | null {
  if (!tokenFrames || !key) return null;
  return tokenFrames[key] ?? null;
}

export function terrainTilesLoaded(terrains: Iterable<string>): boolean {
  for (const t of new Set(terrains)) {
    if (!getTerrainTexture(t)) return false;
  }
  return true;
}

async function tryLoad(url: string): Promise<Record<string, Texture> | null> {
  try {
    const sheet = await Assets.load(url);
    // Pixi Spritesheet exposes a frame→Texture map on `.textures`.
    const textures = (sheet as { textures?: Record<string, Texture> } | undefined)?.textures;
    return textures ?? null;
  } catch {
    // Absent atlas (the default) — fall back to vector rendering.
    return null;
  }
}

// Load one per-terrain PNG (e.g. /atlases/terrain/grass.png). Each tile is
// optional: a missing file returns null, leaving that terrain on its vector
// fill (flat renderer) or its vector iso hex (iso renderer).
async function tryLoadOne(url: string): Promise<Texture | null> {
  try {
    return ((await Assets.load(url)) as Texture | undefined) ?? null;
  } catch {
    return null;
  }
}
